/**
 * Getting slate onto a home screen.
 *
 * Which of four things to say is a judgement, so it is a pure function here
 * rather than a tangle of `navigator` checks inside a component. The sniffing
 * that cannot be pure — reading the user agent and the display mode — is one
 * function at the bottom, called once.
 *
 * There is no service worker and no offline mode. Installing here means a real
 * icon and a window without browser chrome, which is what a shortcut is for;
 * claiming more would be a promise the app does not keep.
 */

export type InstallState =
  /** Already running from the home screen. Nothing to offer. */
  | { kind: 'installed' }
  /** The browser handed us a prompt to fire from a button. */
  | { kind: 'prompt' }
  /** Safari has no prompt to hand over, so the steps have to be written out. */
  | { kind: 'ios' }
  /** Everything else: the browser's own menu carries it. */
  | { kind: 'menu' };

export interface InstallEnv {
  /** Running in a standalone window rather than a browser tab. */
  standalone: boolean;
  /** A `beforeinstallprompt` event has been captured and not yet used. */
  hasPrompt: boolean;
  /** iOS or iPadOS, where the Share sheet is the only route. */
  ios: boolean;
}

/**
 * Installed wins over everything: an install button inside an installed app
 * is at best a no-op and at worst reads as "this didn't work".
 *
 * A captured prompt beats the written instructions even on iOS, where it
 * should never fire — if some future Safari does hand one over, firing it is
 * better than telling someone to do it by hand.
 */
export function installState(env: InstallEnv): InstallState {
  if (env.standalone) return { kind: 'installed' };
  if (env.hasPrompt) return { kind: 'prompt' };
  if (env.ios) return { kind: 'ios' };
  return { kind: 'menu' };
}

/**
 * iPadOS reports itself as a Mac, and has done since 13. The touch count is
 * what tells them apart — a desktop Safari reports 0, an iPad reports 5 — and
 * without it every iPad falls through to the "use your browser's menu"
 * message, which on Safari names a menu item that does not exist.
 */
export function isIosLike(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

/** The one impure read, kept in one place. */
export function readInstallEnv(hasPrompt: boolean): InstallEnv {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const standalone =
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
    // Safari's own, which predates the standard query and is still the only
    // one it answers to.
    (nav as { standalone?: boolean } | undefined)?.standalone === true;

  return {
    standalone,
    hasPrompt,
    ios: isIosLike(nav?.userAgent ?? '', nav?.maxTouchPoints ?? 0),
  };
}
